(ns nosco.ui.toast
  (:require
   [hx.react :as hx]
   ["react-useportal" :default usePortal]
   ["@react-spring/web" :refer [useTransition animated]]
   ["react-keyed-flatten-children" :default flatten-children]
   [nosco.icons2 :as icons2]
   [nosco.react]
   [nosco.util :as util]
   [reseda.state :as rs]
   [reseda.react :as rr]
   [hx.hooks :as hooks]
   [nosco.ui.text :as text]))

(defonce message-ids-counter (atom 0))

;; There is a separate state for error-toasts resulting from a mutation because
;; we don't want those messages to be overriden by toasts resulting from,
;; for example, queries.

(defonce ^:private state-toast-error (atom nil))
(defonce ^:private store-toast-error (rs/new-store state-toast-error))

(defonce ^:private state-toast-info (atom nil))
(defonce ^:private store-toast-info (rs/new-store state-toast-info))

(defonce ^:private state-toast-error-mutation (atom nil))
(defonce ^:private store-toast-error-mutation (rs/new-store state-toast-error-mutation))

(defonce ^:private state-toast-confirmation (atom nil))
(defonce ^:private store-toast-confirmation (rs/new-store state-toast-confirmation))

(def ^:private toast-confirmation-timer (atom nil))
(def ^{:private true :const true} toast-confirmation-timeout 4000)

;; The container the toasts will be attached to. Useful when displaying toast in modals for example.
(defonce state-toast-container (atom []))
(defonce ^:private store-toast-container (rs/new-store state-toast-container))

(defn remove-message [type]
  (case type
    :error (do
             (reset! state-toast-error nil)
             (reset! state-toast-error-mutation nil))

    :info (reset! state-toast-info nil)

    (reset! state-toast-confirmation nil)))

(defn remove-toast-error-mutation-with-id [id]
  (swap! state-toast-error-mutation (fn [toast]
                                      (when-not (= (:id toast) id)
                                        toast))))

(defn- message-with-id [message]
  (let [id (swap! message-ids-counter inc)]
    (assoc message :id id)))

(defn show-message-error [message]
  (reset! state-toast-error (assoc (message-with-id message) :type :error)))

(defn show-message-error-mutation [message]
  (reset! state-toast-error nil)
  (reset! state-toast-error-mutation (assoc (message-with-id message) :type :error)))

(defn- remove-confirmation-message-with-id [toast id]
  (when-not (= (:id toast) id)
    toast))

(defn- remove-confirmation-message-callback! [id]
  (swap! state-toast-confirmation remove-confirmation-message-with-id id))

(defn- clear-confirmation-timer! []
  (when-let [timer @toast-confirmation-timer]
    (js/clearTimeout timer)
    (reset! toast-confirmation-timer nil)))

(defn- set-confirmation-timer! [id]
  (reset! toast-confirmation-timer
          (js/setTimeout (partial remove-confirmation-message-callback! id)
                         toast-confirmation-timeout)))

(defn show-confirmation [{:keys [title]}]
  (let [message (message-with-id {:title title
                                  :type :confirmation})]
    (clear-confirmation-timer!)
    (reset! state-toast-confirmation message)
    (set-confirmation-timer! (:id message))))

(defn show-info-message [message]
  (let [message (message-with-id message)]
    (reset! state-toast-info message)))

(hx/defnc ToastBox [{:keys [children] :as props}]
  [:div
   (-> {:class "nosco-toast pt-3 pb-3 pl-5 pr-5"}
       (nosco.react/merge-props (dissoc props :on-close :title :text)))
   children])

(hx/defnc ToastTitle [{:keys [icon on-close children]}]
  [:div {:class "nosco-toast__title"}
   [:div {:class "nosco-toast__icon"}
    [icons2/Icon {:icon icon
                  :icon-style {:position "relative"
                               :right "24px"}}]]
   children
   (when on-close
     [:div {:class "nosco-toast__close"}
      [:button {:style {:position "relative"
                        :left "16px"}
                :on-click on-close}
       [icons2/Icon {:icon icons2/Close-large}]]])])

(hx/defnc ToastText [{:keys [children]}]
  [text/Text {:variant text/Body-Md-Regular
              :el :div
              :class "mt-2"}
   children])

(hx/defnc ToastActions [{:keys [children]}]
  (into [:div {:class "nosco-toast__actions mt-2"}]
        (interpose [:span {:class "px-2 toast-action-separator"}
                    "\u2022"])
        (flatten-children children)))

(hx/defnc ToastActionButton [{:keys [label] :as props}]
  [:button (-> {:type "button"
                :class ["button-reset toast-action"]}
               (nosco.react/merge-props (dissoc props :label)))
   label])

(hx/defnc ToastActionLink [{:keys [children] :as props}]
  [:a (-> {:class ["toast-action"]} (nosco.react/merge-props props)) children])

(hx/defnc ToastItemConfirmation [{:keys [id title on-close]}]
  [ToastBox {:class "nosco-toast-confirmation"
             :onMouseEnter (fn [e] (clear-confirmation-timer!))
             :onMouseLeave (fn [e] (set-confirmation-timer! id))}
   [ToastTitle {:icon icons2/Check-large :on-close on-close} title]])

(hx/defnc ToastItemError [{:keys [title text actions on-close] :as props}]
  [ToastBox (-> props
                (assoc :class "nosco-toast--critical")
                (dissoc :actions))
   [ToastTitle {:icon icons2/Semantic-Error-large :on-close on-close} title]
   [ToastText text]
   (when (seq actions)
     [ToastActions
      (for [action actions
            :let [{:keys [type title href target rel on-click]} action]]
        (case type
          :button [ToastActionButton {:on-click on-click
                                      :rel rel
                                      :label title}]
          :link [ToastActionLink {:href href
                                  :target target
                                  :rel rel} title]))])])

(hx/defnc ToastItemInfo [{:keys [title text actions on-close] :as props}]
  [ToastBox (-> props
                (assoc :class "nosco-toast--info")
                (dissoc :actions))
   [ToastTitle {:icon icons2/Info-large :on-close on-close} title]
   [ToastText text]
   (when (seq actions)
     [ToastActions
      (for [action actions
            :let [{:keys [type title href target rel on-click close-on-click?]} action]]
        (case type
          :button [ToastActionButton {:on-click on-click
                                      :rel rel
                                      :label title}]
          :link [ToastActionLink {:href href
                                  :target target
                                  :on-click (fn []
                                              (when close-on-click?
                                                (on-close))
                                              (when on-click
                                                (on-click)))} title]))])])

(hx/defnc ToastItem [{:keys [type] :as props}]
  (case type
    :error (let [props (dissoc props :type :error-id)]
             [ToastItemError (dissoc props :id)])
    :info (let [props (dissoc props :type :error-id)]
            [ToastItemInfo (dissoc props :id)])
    :confirmation [ToastItemConfirmation props]))

(defn toast-id [item]
  (str "toast-item-" (:id item)))

(hx/defnc ToastContainer [{:keys [children]}]
  (let [toast-container (rr/useStore store-toast-container identity)
        Portal (.-Portal (usePortal #js {:bindTo (peek toast-container)}))
        toast-error (rr/useStore store-toast-error identity)
        toast-error-mutation (rr/useStore store-toast-error-mutation identity)
        toast-info (rr/useStore store-toast-info identity)
        toast (rr/useStore store-toast-confirmation identity)
        toasts #js []
        _ (cond toast-error-mutation (.push toasts toast-error-mutation)
                toast-error (.push toasts toast-error))
        _ (when toast-info (.push toasts toast-info))
        _ (when toast (.push toasts toast))
        toasts (.sort toasts (fn [x1 x2]
                               (if (< (:id x1) (:id x2))
                                 -1
                                 1)))
        transition (useTransition toasts
                                  #js {:from #js {:opacity 0 :height 0 :overflow "hidden" :display "block" :marginTop 0}
                                       :enter (fn [item]
                                                (fn [next cancel]
                                                  (let [toast-node (js/document.getElementById
                                                                    (toast-id item))
                                                        height (if toast-node
                                                                 (.-offsetHeight toast-node)
                                                                 120)]
                                                    (next #js {:opacity 1
                                                               :height height
                                                               :marginTop (util/rem->px)
                                                                ;; Make the width of toasts dependent on its content instead of
                                                                ;; taking the width of the longest toast
                                                               :display "inline-flex"}))))
                                       :leave #js {:opacity 0 :height 0 :marginTop 0}})]
    [:<>
     children
     [Portal
      [:div {:class "toast-portal"
             :style {:position (if (seq toast-container) "absolute" "fixed")}}
       (transition
        (fn [style item]
          (hx/f
           [(.-div animated) {:key (when item (:id item))
                              :style style
                              :className "text-left"}
            [:div {:id (toast-id item)}
             (let [on-close' (:on-close item)
                   on-close (fn []
                              (remove-message (:type item))
                              (when on-close'
                                (on-close')))]
               [ToastItem (assoc item :on-close on-close)])]])))]]]))

(defn use-toast-context
  "Display toast in a container instead of in the context of the full window"
  ([node-ref deps]
   (use-toast-context node-ref deps true))
  ;; NB React effects may run in-to-out, so the first node that is added to the
  ;; vector may be the inner-most one; Later down we use `peek` to grab it.
  ;; In practice it works because we use "use-toast-context" in modals, so
  ;; effects are run out-to-in. But we may have to revisit the logic here
  ([node-ref deps visible]
   (hooks/useLayoutEffect
    (fn []
      (when visible
        (when-let [node  (.-current node-ref)]
          (swap! state-toast-container conj node)))
      (fn [] (when visible
               (when (.-current node-ref)
                 (swap! state-toast-container pop)))))
    deps)))

(comment
  (show-message-error {:title "Network error"
                       :text "The network connection is lost. Reconnect to the internet."
                       :actions [{:type :button
                                  :title "Refresh Page"
                                  :on-click #()}
                                 {:type :link
                                  :href "href"
                                  :title "Contact Support"}]})

  (show-message-error-mutation {:title "Network error"
                                :text "The network connection is lost. Reconnect to the internet. The network connection is lost. Reconnect to the internet."})

  (show-message {:title "Stage changed"})

  (show-info-message {:title "New feature"
                      :text "Lorem ipsum feature description"
                      :type :info
                      :on-close #(js/console.log "dismissed!")
                      :actions [{:type :button
                                 :title "Refresh Page"
                                 :on-click #()}
                                {:type :link
                                 :href "href"
                                 :title "Contact Support"}]}))
